import {useCallback, useRef} from 'react';
import {useEvent} from './use-event';

export function useDebouncedFunction<Args extends unknown[]>(
  callback: (...args: Args) => void,
  delay: number,
) {
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const immediateCalling = useRef(false);
  const callbackFn = useEvent(callback);

  const throttled = useCallback(
    (...args: Args) => {
      clearTimeout(timerRef.current as unknown as number);

      if (immediateCalling.current) {
        immediateCalling.current = false;
        callbackFn?.(...args);
      } else {
        timerRef.current = setTimeout(() => {
          immediateCalling.current = false;
          callbackFn?.(...args);
        }, delay);
      }
    },
    [callbackFn, delay],
  );

  const immediate = useCallback(() => {
    immediateCalling.current = true;
  }, []);

  return {throttled, immediate};
}
